/**
 * 统一请求封装。
 * 开发环境 /api 经 CRA 代理转发到 NestJS；
 * 生产环境与站点同源，由 Nginx 反向代理。
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL ?? '';
const TOKEN_KEY = 'qcg_admin_token';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  window.localStorage.removeItem(TOKEN_KEY);
}

/** 将后端返回的资源相对路径转换为完整可访问地址 */
export function assetUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url}`;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  auth?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);

  if (auth !== false && getToken()) {
    finalHeaders.set('Authorization', `Bearer ${getToken()}`);
  }

  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) {
    finalHeaders.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      headers: finalHeaders,
      body: body !== undefined ? (isForm ? (body as FormData) : JSON.stringify(body)) : undefined,
    });
  } catch {
    throw new ApiError(0, '网络连接异常，请稍后重试');
  }

  if (response.status === 204) return undefined as T;

  let data: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    const message =
      (data as { message?: string | string[] } | null)?.message instanceof Array
        ? (data as { message: string[] }).message.join('；')
        : ((data as { message?: string } | null)?.message ?? '请求失败，请稍后重试');
    throw new ApiError(response.status, message);
  }

  return data as T;
}

/** 上传图片（multipart/form-data），返回可访问 URL */
export async function uploadImage(file: File): Promise<{ url: string; size: number }> {
  const formData = new FormData();
  formData.append('file', file);
  return request<{ url: string; size: number }>('/api/uploads', {
    method: 'POST',
    body: formData as unknown,
  });
}
